"use client";

import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";

/**
 * Champ « Raison sociale » avec autocomplétion.
 *
 * Source : recherche-entreprises.api.gouv.fr — annuaire officiel des
 * entreprises (base SIRENE), gratuit, sans clé ni quota. France uniquement.
 *
 * Choisir une entreprise remplit d'un coup la raison sociale, le SIRET,
 * l'adresse du siège, ET le numéro de TVA — calculé depuis le SIREN par la
 * formule officielle, aucun appel réseau de plus. La saisie reste libre pour
 * une société étrangère ou introuvable.
 */

interface Entreprise {
  nom: string;
  siren: string;
  siret: string;
  adresse: string; // rue seule
  codePostal: string;
  ville: string;
  tva: string; // FR + clé + SIREN
}

interface Props {
  value: string;
  onChange: (nom: string) => void;
  onSelect: (e: {
    companyName: string;
    siret: string;
    vatNumber: string;
    address: string;
    postalCode: string;
    city: string;
  }) => void;
  id?: string;
  placeholder?: string;
  className?: string;
}

/**
 * Numéro de TVA intracommunautaire français : « FR » + clé + SIREN.
 * clé = (12 + 3 × (SIREN mod 97)) mod 97, sur deux chiffres.
 */
function tvaDepuisSiren(siren: string): string {
  const n = Number(siren);
  if (!Number.isInteger(n) || siren.length !== 9) return "";
  const cle = (12 + 3 * (n % 97)) % 97;
  return `FR${String(cle).padStart(2, "0")}${siren}`;
}

/** Retire le « CP VILLE » final de l'adresse complète pour n'en garder que la rue. */
function rueSeule(adresse: string, cp: string, ville: string): string {
  const suffixe = `${cp} ${ville}`;
  return adresse.endsWith(suffixe)
    ? adresse.slice(0, adresse.length - suffixe.length).trim()
    : adresse;
}

export function CompanyAutocomplete({
  value,
  onChange,
  onSelect,
  id,
  placeholder,
  className,
}: Props) {
  const [suggestions, setSuggestions] = useState<Entreprise[]>([]);
  const [ouvert, setOuvert] = useState(false);
  const vientDeChoisir = useRef(false);
  const boiteRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (vientDeChoisir.current) {
      vientDeChoisir.current = false;
      return;
    }
    const q = value.trim();
    const annule = new AbortController();
    if (q.length < 3) {
      const vide = setTimeout(() => setSuggestions([]), 0);
      return () => clearTimeout(vide);
    }
    const t = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://recherche-entreprises.api.gouv.fr/search?q=${encodeURIComponent(q)}&per_page=5`,
          { signal: annule.signal }
        );
        if (!res.ok) return;
        const data = await res.json();
        const items: Entreprise[] = (data.results ?? []).map(
          (r: {
            nom_complet?: string;
            nom_raison_sociale?: string;
            siren?: string;
            tva?: string[] | string | null;
            siege?: { siret?: string; adresse?: string; code_postal?: string; libelle_commune?: string };
          }) => {
            const s = r.siege ?? {};
            const cp = s.code_postal ?? "";
            const ville = s.libelle_commune ?? "";
            // TVA fournie par l'API en priorité (source officielle) ; à défaut,
            // calculée depuis le SIREN. La formule concorde avec l'API, mais un
            // numéro déjà validé vaut mieux qu'un numéro reconstruit.
            const tvaApi = Array.isArray(r.tva) ? r.tva[0] : r.tva ?? undefined;
            return {
              nom: r.nom_raison_sociale || r.nom_complet || "",
              siren: r.siren ?? "",
              siret: s.siret ?? "",
              adresse: rueSeule(s.adresse ?? "", cp, ville),
              codePostal: cp,
              ville,
              tva: tvaApi || tvaDepuisSiren(r.siren ?? ""),
            };
          }
        );
        setSuggestions(items);
        setOuvert(true);
      } catch {
        // Réseau coupé / annulé : saisie manuelle.
      }
    }, 250);
    return () => {
      clearTimeout(t);
      annule.abort();
    };
  }, [value]);

  useEffect(() => {
    const dehors = (e: MouseEvent) => {
      if (boiteRef.current && !boiteRef.current.contains(e.target as Node)) {
        setOuvert(false);
      }
    };
    document.addEventListener("mousedown", dehors);
    return () => document.removeEventListener("mousedown", dehors);
  }, []);

  const choisir = (e: Entreprise) => {
    vientDeChoisir.current = true;
    onSelect({
      companyName: e.nom,
      siret: e.siret,
      vatNumber: e.tva,
      address: e.adresse,
      postalCode: e.codePostal,
      city: e.ville,
    });
    setOuvert(false);
    setSuggestions([]);
  };

  return (
    <div ref={boiteRef} className="relative">
      <Input
        id={id}
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => suggestions.length > 0 && setOuvert(true)}
        className={className}
      />
      {ouvert && suggestions.length > 0 && (
        <div className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-border bg-[var(--bg-secondary)] shadow-lg">
          {suggestions.map((e, i) => (
            <button
              key={`${e.siren}-${i}`}
              type="button"
              onClick={() => choisir(e)}
              className="flex w-full flex-col items-start px-3 py-2 text-left text-sm hover:bg-accent"
            >
              <span className="text-foreground">{e.nom}</span>
              <span className="text-xs text-muted-foreground">
                {[e.codePostal && e.ville ? `${e.codePostal} ${e.ville}` : "", e.siret ? `SIRET ${e.siret}` : ""]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
